<!-- Large Dialog -->
<umss-dialog
  [open]="open()"
  size="Large"
  type="Responsive"
  position="Center"
  [backdrop]="true"
  [backdropClosable]="
    !isProcessingAction() && !isLoadingInitialEntry() && !isLoadingContent()
  "
  (onClose)="closeDialog()"
>
  <umss-dialog-header
    title="Timesheet Details"
    [showCloseButton]="true"
    (onClose)="closeDialog()"
  />

  <div class="dialog-body">
    @if (isLoadingContent() || isLoadingInitialEntry()) {
      <!-- Loading skeleton -->
      <div class="skeleton-wrapper">
        <!-- Metadata Section -->
        <div class="skeleton-block">
          <umss-skeleton variant="Line" [spacingY]="5" />
          <umss-skeleton
            variant="Line"
            [spacingY]="4"
            [styles]="{ width: '60%' }"
          />
        </div>

        <div class="skeleton-block">
          <umss-skeleton variant="Line" [spacingY]="5" />
          <umss-skeleton
            variant="Line"
            [spacingY]="4"
            [styles]="{ width: '50%' }"
          />
        </div>

        <!-- Shift Information Section -->
        <div class="skeleton-section-title">
          <umss-skeleton
            variant="Line"
            [spacingY]="4"
            [styles]="{ width: '25%' }"
          />
        </div>

        <div class="skeleton-block">
          <umss-skeleton variant="Line" [spacingY]="5" />
          <umss-skeleton
            variant="Line"
            [spacingY]="4"
            [styles]="{ width: '40%' }"
          />
        </div>

        <div class="skeleton-block">
          <umss-skeleton variant="Line" [spacingY]="5" />
          <umss-skeleton
            variant="Line"
            [spacingY]="4"
            [styles]="{ width: '45%' }"
          />
        </div>

        <div class="skeleton-block">
          <umss-skeleton variant="Line" [spacingY]="5" />
          <umss-skeleton
            variant="Line"
            [spacingY]="4"
            [styles]="{ width: '55%' }"
          />
        </div>

        <!-- Billable Hours Section -->
        <div class="skeleton-section-title">
          <umss-skeleton
            variant="Line"
            [spacingY]="4"
            [styles]="{ width: '30%' }"
          />
        </div>

        <div class="skeleton-box">
          <umss-skeleton variant="Line" [spacingY]="5" />
          <umss-skeleton variant="Line" [spacingY]="5" />
          <umss-skeleton variant="Line" [spacingY]="5" />
        </div>
      </div>
    } @else {
      <div class="metadata">
        <div>
          <div class="attribute">
            <p class="attribute-subtitle pg-small-medium">Timesheet #</p>
            <p class="pg-medium">
              {{ timesheet().timesheetNumberDisplay || '--' }}
            </p>
          </div>
          <div class="attribute">
            <p class="attribute-subtitle pg-small-medium">Status</p>
            @if (badgeProps[timesheet().timesheetStatus]; as badge) {
              <umss-badge
                [iconName]="badge.iconName"
                [label]="badge.label"
                [variant]="badge.variant"
                [outlined]="badge.outlined"
              />
            }
          </div>
        </div>
        <div>
          <div class="attribute">
            <p class="attribute-subtitle pg-small-medium">Professional</p>
            <p class="pg-medium">{{ timesheet().contractorName || '--' }}</p>
          </div>
          <div class="attribute">
            <p class="attribute-subtitle pg-small-medium">Hourly Rate</p>
            <p class="pg-medium">
              {{
                timesheet().hourlyRate
                  ? (timesheet().hourlyRate | currency) + '/hr'
                  : '--'
              }}
            </p>
          </div>
        </div>
      </div>

      <div class="shift-information">
        <h4>Shift Information</h4>
        <div>
          <div class="attribute">
            <p class="attribute-subtitle pg-small-medium">Shift Date</p>
            <p class="pg-medium">
              {{ (timesheet().shiftDate | date: 'MMMM dd, yyyy') || '--' }}
            </p>
          </div>
          <div class="attribute">
            <p class="attribute-subtitle pg-small-medium">Facility</p>
            <p class="pg-medium">{{ timesheet().facilityName || '--' }}</p>
          </div>
        </div>
        <div>
          <div class="attribute">
            <p class="attribute-subtitle pg-small-medium">Shift Time</p>
            <p class="pg-medium">
              {{
                ([timesheet().startTime, timesheet().endTime] | timeRange) ||
                  '--'
              }}
            </p>
          </div>
          <div class="attribute">
            <p class="attribute-subtitle pg-small-medium">Supervisor</p>
            <p class="pg-medium">
              {{ timesheet().supervisorName || '--' }}
            </p>
          </div>
        </div>
        <div>
          @if (shift()) {
            <div class="attribute">
              <div class="attribute-subtitle pg-small-medium">
                Clock in / Clock out
              </div>
              <div class="pg-medium">
                {{
                  [
                    shift()!.actualStartTime || timesheet().startTime,
                    shift()!.actualEndTime || timesheet().endTime
                  ] | timeRange
                }}
              </div>
            </div>
          }
          <div class="attribute">
            <p class="attribute-subtitle pg-small-medium">Break time</p>
            <p class="pg-medium">
              {{
                timesheet().breakDurationMinutes
                  ? timesheet().breakDurationMinutes + 'm (unpaid)'
                  : '--'
              }}
            </p>
          </div>
        </div>
        @if (timesheet().clockInNote) {
          <div>
            <div class="attribute">
              <div class="attribute-subtitle pg-small-medium">
                Clock-in Note
              </div>
              <div class="pg-medium">{{ timesheet().clockInNote }}</div>
            </div>
          </div>
        }
        @if (timesheet().clockOutNote) {
          <div>
            <div class="attribute">
              <div class="attribute-subtitle pg-small-medium">
                Clock-out Note
              </div>
              <div class="pg-medium">{{ timesheet().clockOutNote }}</div>
            </div>
          </div>
        }
      </div>
      <div class="billable-hours">
        <h4>Billable Hours</h4>
        <div class="info-box">
          <div class="time">
            <p class="pg-regular">Total Shift Time</p>
            <p class="pg-medium">
              {{
                {
                  hours: timesheet().hoursWorked,
                  minutes: timesheet().breakDurationMinutes
                } | timeFormat
              }}
            </p>
          </div>
          <div class="time">
            <p class="pg-regular">Break Time (unpaid)</p>
            <p class="pg-medium">
              -{{ { minutes: timesheet().breakDurationMinutes } | timeFormat }}
            </p>
          </div>
          <div class="time">
            <h4>Total Billable Hours</h4>
            <h4 class="heading-4">
              {{ { hours: timesheet().hoursWorked } | timeFormat }}
            </h4>
          </div>
        </div>
        @if (shiftNeed()) {
          <div class="info-box">
            <div class="time">
              <p class="pg-regular">Professional hourly rate</p>
              <p class="pg-medium">
                {{ contractorHourlyRate() | currency: 'USD' }}
              </p>
            </div>
            <div class="time">
              <p class="pg-regular">Differential hourly rate</p>
              <p class="pg-medium">
                +{{ differentialHourlyRate() | currency: 'USD' }}
              </p>
            </div>
            @if (showBonusRow()) {
              <div class="time">
                <p class="pg-regular">Bonus</p>
                <p class="pg-medium">
                  +{{ bonusHourlyRate() | currency: 'USD' }}/hr
                </p>
              </div>
            }
            <div class="time">
              <h4>Total differential hourly rate</h4>
              <p class="pg-regular">
                {{ timesheet().hoursWorked }}h&nbsp;x&nbsp;
                {{ differentialHourlyRate() | currency: 'USD' }}
                /hr
              </p>
              <h4 class="heading-4">
                {{ totalDifferentialAmount() | currency: 'USD' }}
              </h4>
            </div>
          </div>
        }

        <div class="info-box">
          <div class="time">
            <h4 class="heading-4">Total:</h4>
            @if (timesheet().hourlyRate && timesheet().hoursWorked) {
              <p class="pg-regular">
                ({{ { hours: timesheet().hoursWorked } | timeFormat }}
                &nbsp;x&nbsp;
                {{ contractorHourlyRate() | currency: 'USD' }}
                )&nbsp;+&nbsp;
                {{ totalDifferentialAmount() | currency: 'USD' }}
                @if (showBonusRow()) {
                  &nbsp;+&nbsp;
                  {{ totalBonusAmount() | currency: 'USD' }}
                }
              </p>
            }
            <h4 class="heading-4">
              {{
                timesheet().hoursWorked * timesheet().hourlyRate
                  | currency: 'USD'
              }}
            </h4>
          </div>
        </div>
      </div>
      <div class="rejection">
        <p class="pg-small-medium">
          Rejection reason (this reason will be shared with the medical
          professional)
        </p>
        <umss-textarea
          [placeholder]="'Rejection reason'"
          [resize]="'vertical'"
          [formControl]="rejectionReasonFC"
        />
        <p class="error-message">
          @if (rejectingWithoutReason() === true) {
            You need to add a reason if you want to reject this Timesheet
          }
        </p>
      </div>
    }
  </div>

  @if (!isLoadingInitialEntry() && !isLoadingContent()) {
    <footer>
      <umss-button
        [label]="'Cancel'"
        [variant]="'Secondary'"
        (onClick)="closeDialog()"
        [loading]="isProcessingAction()"
      />

      <umss-button
        *umssHasPermission="'approve_timesheets'"
        [label]="'Reject'"
        [variant]="'Destructive'"
        [umssTooltip]="
          timesheet().timesheetStatus !== 'Pending'
            ? 'The timesheet has to has a pending status to reject'
            : ''
        "
        [leftIconName]="'phosphorXCircle'"
        [disabled]="timesheet().timesheetStatus !== 'Pending'"
        [loading]="isProcessingAction()"
        (onClick)="reject()"
      />

      <umss-button
        *umssHasPermission="'approve_timesheets'"
        [label]="'Approve'"
        [variant]="'Primary'"
        [umssTooltip]="
          timesheet().timesheetStatus === 'Approved'
            ? 'The timesheet has to has a pending status to approve'
            : ''
        "
        [leftIconName]="'phosphorCheckCircle'"
        [disabled]="timesheet().timesheetStatus !== 'Pending'"
        [loading]="isProcessingAction()"
        (onClick)="approve()"
      />
    </footer>
  }
</umss-dialog>
